import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Loader2, Music } from "lucide-react";

// Official "Music at The D" track. Never autoplays — starts only when the visitor presses Play.
// Loops, default volume 30%, preload metadata, remembers playback/volume for the browsing session.
const SRC = "/assets/media/music-at-the-d.mp3";
const PKEY = "thed_music_playing";
const VKEY = "thed_music_vol";
const MKEY = "thed_music_muted";

export default function AmbientSound() {
  const audioRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [muted, setMuted] = useState(() => sessionStorage.getItem(MKEY) === "1");
  const [vol, setVol] = useState(() => {
    const v = parseFloat(sessionStorage.getItem(VKEY));
    return Number.isFinite(v) ? v : 0.3;
  });

  useEffect(() => {
    const a = new Audio();
    a.src = SRC;
    a.loop = true;
    a.preload = "metadata";
    a.volume = vol;
    a.muted = muted;
    audioRef.current = a;
    const onPlaying = () => { setLoading(false); setPlaying(true); };
    const onPause = () => setPlaying(false);
    const onWaiting = () => setLoading(true);
    const onError = () => { setError(true); setLoading(false); setPlaying(false); };
    a.addEventListener("playing", onPlaying);
    a.addEventListener("pause", onPause);
    a.addEventListener("waiting", onWaiting);
    a.addEventListener("error", onError);
    // Resume within the same session only if the visitor already pressed Play earlier (prior gesture).
    if (sessionStorage.getItem(PKEY) === "1") {
      setLoading(true); setExpanded(true);
      a.play().then(() => {}).catch(() => { setLoading(false); setPlaying(false); });
    }
    return () => {
      a.pause();
      a.removeEventListener("playing", onPlaying);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("waiting", onWaiting);
      a.removeEventListener("error", onError);
      audioRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const play = () => {
    const a = audioRef.current; if (!a) return;
    setLoading(true); setExpanded(true);
    a.play().then(() => sessionStorage.setItem(PKEY, "1"))
      .catch(() => { setError(true); setLoading(false); });
  };
  const pause = () => {
    const a = audioRef.current; if (!a) return;
    a.pause(); sessionStorage.setItem(PKEY, "0");
  };
  const toggle = () => (playing ? pause() : play());
  const toggleMute = () => {
    const a = audioRef.current; if (!a) return;
    const m = !muted; a.muted = m; setMuted(m); sessionStorage.setItem(MKEY, m ? "1" : "0");
  };
  const changeVol = (v) => {
    const a = audioRef.current; setVol(v); sessionStorage.setItem(VKEY, String(v));
    if (a) { a.volume = v; if (v > 0 && muted) { a.muted = false; setMuted(false); sessionStorage.setItem(MKEY, "0"); } }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2" data-testid="music-control">
      {error ? (
        <div className="glass rounded-full px-4 h-12 flex items-center gap-2 text-xs text-muted-foreground" data-testid="music-unavailable" title="Music unavailable">
          <Music size={16} className="text-gold/60" /> Music unavailable
        </div>
      ) : (
        <>
          {(playing || expanded) && (
            <div className="glass rounded-full pl-3 pr-2 h-12 flex items-center gap-2" data-testid="music-panel">
              <span className="text-[11px] tracking-label uppercase text-gold hidden sm:inline whitespace-nowrap" data-testid="music-label">Music at The D</span>
              <button onClick={toggleMute} aria-label={muted ? "Unmute Music at The D" : "Mute Music at The D"} aria-pressed={muted} data-testid="music-mute" className="text-gold p-1 hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded">
                {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
              <input type="range" min="0" max="1" step="0.05" value={muted ? 0 : vol}
                onChange={(e) => changeVol(parseFloat(e.target.value))}
                aria-label="Music volume" data-testid="music-volume"
                className="w-20 accent-gold cursor-pointer" />
            </div>
          )}
          <button onClick={toggle} disabled={loading && !playing}
            data-testid="music-toggle"
            aria-label={playing ? "Pause Music at The D" : "Play Music at The D"} aria-pressed={playing}
            title="Music at The D"
            className="h-12 w-12 rounded-full glass flex items-center justify-center text-gold hover:glow-gold transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:opacity-60">
            {loading ? <Loader2 size={18} className="animate-spin" /> : playing ? <Pause size={18} /> : <Play size={18} />}
          </button>
        </>
      )}
    </div>
  );
}
